import axiosClient from 'utils/axiosClient';
import api from 'api';
import {formData} from 'utils/helpers/formData';

const clubPath = (clubID: any, suffix: string = '') => api.CLUBS.CLUBS_BY_ID + clubID + suffix;

// Clubs, announcements and posts take an optional `image`. A newly picked one is a File and
// has to go as multipart; without one the body stays JSON as before. An untouched image comes
// back as its stored path, which is not re-sent.
const withImage = ({image, ...data}: any, type?: 'ADD') =>
	image instanceof File ? formData({...data, image}, type) : data;

/*
 *** Clubs
 */

const getClubs = async (params?: any) => {
	const response = await axiosClient.get(api.CLUBS.CLUBS, {params});
	return response.data;
};

// Swagger documents a bare club object here, while the rest of the app reads `res.data`.
// Normalising keeps CRUDTable's getFieldsValues and the detail page working either way.
const getClubByID = async (clubID: any) => {
	const response = await axiosClient.get(clubPath(clubID));
	return response.data?.data ? response.data : {data: response.data};
};

const createClub = async (data: any) => {
	const response = await axiosClient.post(api.CLUBS.CLUBS, withImage(data, 'ADD'));
	return response.data;
};

const updateClub = async (clubID: any, data: any) => {
	const response = await axiosClient.patch(clubPath(clubID), withImage(data));
	return response.data;
};

// The endpoint deletes a single club, while CRUDTable passes comma joined ids.
const deleteClubs = async (ids: any) => {
	const idArray = String(ids)
		.split(',')
		.map(id => id.trim())
		.filter(Boolean);
	const responses = await Promise.all(idArray.map(id => axiosClient.delete(clubPath(id))));
	return responses[responses.length - 1]?.data;
};

/*
 *** Announcements
 */

const getClubAnnouncements = async (clubID: any, params?: any) => {
	const response = await axiosClient.get(clubPath(clubID, '/announcements'), {params});
	return response.data;
};

const createClubAnnouncement = async (clubID: any, data: any) => {
	const response = await axiosClient.post(clubPath(clubID, '/announcements'), withImage(data, 'ADD'));
	return response.data;
};

const updateClubAnnouncement = async (clubID: any, announcementID: any, data: any) => {
	const response = await axiosClient.patch(
		clubPath(clubID, `/announcements/${announcementID}`),
		withImage(data)
	);
	return response.data;
};

const deleteClubAnnouncement = async (clubID: any, announcementID: any) => {
	const response = await axiosClient.delete(clubPath(clubID, `/announcements/${announcementID}`));
	return response.data;
};

// Comments are sent over the socket (`club:*-comment-send`) and read here: these
// rows carry senderName and studentImage, which the socket pushes leave out.
const getClubAnnouncementComments = async (clubID: any, announcementID: any, params?: any) => {
	const response = await axiosClient.get(clubPath(clubID, `/announcements/${announcementID}/comments`), {
		params
	});
	return response.data;
};

const deleteClubAnnouncementComment = async (clubID: any, announcementID: any, commentID: any) => {
	const response = await axiosClient.delete(
		clubPath(clubID, `/announcements/${announcementID}/comments/${commentID}`)
	);
	return response.data;
};

/*
 *** Join requests
 */

const getClubJoinRequests = async (clubID: any, params?: any) => {
	const response = await axiosClient.get(clubPath(clubID, '/join-requests'), {params});
	return response.data;
};

const approveClubJoinRequest = async (clubID: any, requestID: any) => {
	const response = await axiosClient.post(clubPath(clubID, `/join-requests/${requestID}/approve`));
	return response.data;
};

const rejectClubJoinRequest = async (clubID: any, requestID: any, data?: any) => {
	const response = await axiosClient.post(clubPath(clubID, `/join-requests/${requestID}/reject`), data);
	return response.data;
};

/*
 *** Members
 */

const getClubMembers = async (clubID: any, params?: any) => {
	const response = await axiosClient.get(clubPath(clubID, '/members'), {params});
	return response.data;
};

// Adds graduates in one call. Already-members are skipped; an unknown ID fails the call (404).
const addClubMembers = async (clubID: any, studentIDs: number[]) => {
	const response = await axiosClient.post(clubPath(clubID, '/members'), {studentIDs});
	return response.data;
};

const deleteClubMember = async (clubID: any, studentID: any) => {
	const response = await axiosClient.delete(clubPath(clubID, `/members/${studentID}`));
	return response.data;
};

/*
 *** Messages
 */

const getClubMessages = async (clubID: any, params?: any) => {
	const response = await axiosClient.get(clubPath(clubID, '/messages'), {params});
	return response.data;
};

const sendClubMessage = async (clubID: any, data: any) => {
	const response = await axiosClient.post(clubPath(clubID, '/messages'), withImage(data, 'ADD'));
	return response.data;
};

const getClubMessageComments = async (clubID: any, messageID: any, params?: any) => {
	const response = await axiosClient.get(clubPath(clubID, `/messages/${messageID}/comments`), {params});
	return response.data;
};

const deleteClubMessage = async (clubID: any, messageID: any) => {
	const response = await axiosClient.delete(clubPath(clubID, `/messages/${messageID}`));
	return response.data;
};

const deleteClubMessageComment = async (clubID: any, messageID: any, commentID: any) => {
	const response = await axiosClient.delete(
		clubPath(clubID, `/messages/${messageID}/comments/${commentID}`)
	);
	return response.data;
};

/*
 *** Notifications
 */

const getClubNotifications = async (clubID: any, params?: any) => {
	const response = await axiosClient.get(clubPath(clubID, '/notifications'), {params});
	return response.data;
};

const sendClubNotification = async (clubID: any, data: any) => {
	const response = await axiosClient.post(clubPath(clubID, '/notifications'), data);
	return response.data;
};

const service = {
	getClubs,
	getClubByID,
	createClub,
	updateClub,
	deleteClubs,
	getClubAnnouncements,
	createClubAnnouncement,
	updateClubAnnouncement,
	deleteClubAnnouncement,
	getClubAnnouncementComments,
	deleteClubAnnouncementComment,
	getClubJoinRequests,
	approveClubJoinRequest,
	rejectClubJoinRequest,
	getClubMembers,
	addClubMembers,
	deleteClubMember,
	getClubMessages,
	sendClubMessage,
	getClubMessageComments,
	deleteClubMessage,
	deleteClubMessageComment,
	getClubNotifications,
	sendClubNotification
};

export default service;
